/**
 * How an email, Slack, or CRM source is NAMED on screen.
 *
 * Two ref families carry these sources — the `[[evt:]]` evidence trail
 * (`types/evidence.ts`, rendered by the EvidencePanel cards) and the citation
 * contract (`types/citationArtifact.ts`, rendered by the EvidencePeek chips).
 * They are separate types with the same shape for the fields that matter here,
 * so the label rules live in one module and both families structurally satisfy
 * its parameter types. A label that reads one way in the panel and another way
 * on a chip is a bug this file exists to prevent.
 *
 * The rules, in one place:
 *
 * - A Slack thread is its channel, with a leading `#`.
 * - An email thread is its subject.
 * - `thread_id` is an ingestion slug ("cedar-rr-revolver-diligence") and is the
 *   LAST resort. A projection filename ("Cedar_email_thread.md") or a hashed
 *   name is never a label at all — neither is reachable from here, and that is
 *   deliberate: these functions read locator fields, not file names.
 * - A CRM value is located by row › object › field, with an as-of stamp when
 *   the snapshot's sync date is known.
 *
 * Mirrors `backend/app/modules/agentic/engine/evidence_markers.py`, which
 * applies the same rules when it renders these refs into answer prose.
 */

/** The comms fields any producer supplies; extra fields are ignored. */
export interface CommsLabelFields {
	source_system: "email" | "slack";
	thread_id: string;
	message_id: string;
	sender?: string | null;
	sent_at?: string | null;
	thread_label?: string | null;
	message_ordinal?: number | null;
	file_id?: number | null;
}

/** The record fields any producer supplies; extra fields are ignored. */
export interface RecordLabelFields {
	source_system: string;
	object_type: string;
	record_id: string;
	field: string;
	record_label?: string | null;
	as_of?: string | null;
}

const SYSTEM_DISPLAY_NAMES: Record<string, string> = {
	dealcloud: "DealCloud",
	crm: "CRM",
	email: "Email",
	slack: "Slack",
	outlook: "Outlook",
	box: "Box",
	drive: "Google Drive",
	sharepoint: "SharePoint",
	upload: "Upload",
};

/** "dealcloud" → "DealCloud". An unknown slug is capitalised, never dropped. */
export const displaySystemName = (raw: string | null | undefined): string => {
	if (!raw) return "Source";
	return SYSTEM_DISPLAY_NAMES[raw.toLowerCase()] ?? raw[0].toUpperCase() + raw.slice(1);
};

/** The ISO date part of a timestamp, or `null` if it isn't ISO-8601. */
export const isoDate = (value: string | null | undefined): string | null => {
	const match = /^(\d{4}-\d{2}-\d{2})/.exec(value ?? "");
	return match ? match[1] : null;
};

/**
 * What the thread is called: "#deal-cedar-rr-revolver", or an email subject.
 * Falls back to the durable container id, prefixed by its system so the slug
 * at least reads as an identifier rather than as a name.
 */
export const commsThreadName = (ref: CommsLabelFields): string => {
	if (ref.thread_label) {
		return ref.source_system === "slack" && !ref.thread_label.startsWith("#")
			? `#${ref.thread_label}`
			: ref.thread_label;
	}
	const system = ref.source_system === "slack" ? "Slack" : "Email";
	return `${system} ${ref.thread_id || ref.message_id}`;
};

/** The message's position in the thread, e.g. "msg 6" — or `null` if unknown. */
export const commsMessageAnchor = (ref: CommsLabelFields): string | null =>
	ref.message_ordinal != null ? `msg ${ref.message_ordinal}` : null;

/** Thread name + anchor: "#deal-cedar-rr-revolver · msg 6". */
export const commsChipLabel = (ref: CommsLabelFields): string =>
	[commsThreadName(ref), commsMessageAnchor(ref)].filter(Boolean).join(" · ");

/** Full descriptor for a tooltip: thread, anchor, sender, date. */
export const commsTitle = (ref: CommsLabelFields): string =>
	[
		commsThreadName(ref),
		commsMessageAnchor(ref),
		ref.sender || null,
		isoDate(ref.sent_at),
	]
		.filter(Boolean)
		.join(" · ");

/**
 * Deep link to the projection document the message lives in, anchored at the
 * message. `null` when the ref does not name a document — the chip then renders
 * unlinked rather than as a link we know would dead-end.
 */
export const commsHref = (ref: CommsLabelFields): string | null => {
	if (ref.file_id == null) return null;
	const base = `doppler://doc/${ref.file_id}`;
	return ref.message_ordinal != null ? `${base}?message=${ref.message_ordinal}` : base;
};

/**
 * The CRM locator: "CED-003 › facilities › commitment_usd".
 *
 * A record-set locator (an aggregate over matching rows — see the backend's
 * `structured/adapter.py`) carries a `query:<sha256>` pseudo id and `field:
 * "*"`. The hash means nothing to a reader, so the set is named instead.
 */
export const recordLocator = (ref: RecordLabelFields): string => {
	if (ref.record_id.startsWith("query:")) {
		return `${ref.object_type} (matching records)`;
	}
	const row = ref.record_label ? `${ref.record_label} (${ref.record_id})` : ref.record_id;
	const parts = [row, ref.object_type];
	if (ref.field && ref.field !== "*") parts.push(ref.field);
	return parts.join(" › ");
};

/** "as of 2026-08-01", or `null` when the snapshot's sync date is unknown. */
export const recordAsOf = (ref: RecordLabelFields): string | null => {
	const date = isoDate(ref.as_of);
	return date ? `as of ${date}` : null;
};

/** System + locator: "DealCloud · CED-003 › facilities › commitment_usd". */
export const recordChipLabel = (ref: RecordLabelFields): string =>
	`${displaySystemName(ref.source_system)} · ${recordLocator(ref)}`;
